import mainApiClient from "../../helpers/services/main-api-client";
import { GetWebsiteFormsResponse, GetWebsitePageResponse } from "../interfaces/api/get-website-page";
import { GetWebsitePagesResponse } from "../interfaces/api/get-website-pages";
import { PostGenerateAiRequest, PostGenerateAiResponse } from "../interfaces/api/post-generate-ai";
import { PostGenerateAiHtmlRequest, PostGenerateAiHtmlResponse } from "../interfaces/api/post-generate-ai-html";
import { PostWebsitePageRequest, PostWebsitePageResponse } from "../interfaces/api/post-website-page";
import {
  PutWebsitePageDomainRequest,
  PutWebsitePageRequest,
  PutWebsitePageResponse,
} from "../interfaces/api/put-website-page";
import { websitePageMapper } from "./mapper";

export const websitePageApiService = {
  getPages(companyId: string) {
    return mainApiClient()
      .get<GetWebsitePagesResponse>("website/pages", { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToPages(data));
  },
  getPage(companyId: string, pageId: string) {
    return mainApiClient()
      .get<GetWebsitePageResponse>(`website/pages/${pageId}`, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToPage(data));
  },
  getForms(companyId: string) {
    return mainApiClient()
      .get<GetWebsiteFormsResponse>("website/pages/forms", { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToForms(data));
  },
  createPage(companyId: string, body: PostWebsitePageRequest) {
    return mainApiClient()
      .post<PostWebsitePageResponse>("website/pages", body, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToCreatedPage(data));
  },
  updatePage(companyId: string, pageId: string, body: PutWebsitePageRequest) {
    return mainApiClient()
      .put<PutWebsitePageResponse>(`website/pages/${pageId}`, body, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToUpdatedPage(data));
  },
  deletePage(companyId: string, pageId: string) {
    return mainApiClient()
      .delete(`website/pages/${pageId}`, { params: { companyId } })
      .then(({ data }) => data);
  },
  publishPage(companyId: string, pageId: string) {
    return mainApiClient()
      .put<PutWebsitePageResponse>(`website/pages/${pageId}/publish`, {}, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToUpdatedPage(data));
  },
  unpublishPage(companyId: string, pageId: string) {
    return mainApiClient()
      .put<PutWebsitePageResponse>(`website/pages/${pageId}/unpublish`, {}, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToUpdatedPage(data));
  },
  updateDomain(companyId: string, pageId: string, body: PutWebsitePageDomainRequest) {
    return mainApiClient()
      .put<PutWebsitePageResponse>(`website/pages/${pageId}/domain`, body, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToUpdatedPage(data));
  },
  verifyDomain(companyId: string, pageId: string) {
    return mainApiClient()
      .post<PutWebsitePageResponse>(`website/pages/${pageId}/verify-domain`, {}, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToUpdatedPage(data));
  },
  generateAi(body: PostGenerateAiRequest, companyId: string) {
    return mainApiClient()
      .post<PostGenerateAiResponse>("website/pages/generate-ai", body, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToAiContent(data));
  },
  generateAiHtml(body: PostGenerateAiHtmlRequest, companyId: string) {
    return mainApiClient()
      .post<PostGenerateAiHtmlResponse>("website/pages/generate-ai-html", body, { params: { companyId } })
      .then(({ data }) => websitePageMapper.mapResponseToAiContent(data));
  },
  uploadImage(companyId: string, file: File) {
    const formData = new FormData();
    formData.append("images", file);
    return mainApiClient()
      .post<{ message: string; payload: { images: { url: string; key: string }[] } }>(
        "website/pages/upload-image",
        formData,
        {
          params: { companyId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      )
      .then(({ data }) => data.payload.images[0]);
  },
  getPreviewHtml(companyId: string, pageId: string) {
    return mainApiClient()
      .get<string>("public/website/page/preview", {
        params: { companyId, pageId },
        responseType: "text" as "json",
      })
      .then(({ data }) => data);
  },
  getPublicPageHtml(companyId: string, slug: string) {
    return mainApiClient()
      .get<string>("public/website/page", {
        params: { companyId, slug },
        responseType: "text" as "json",
      })
      .then(({ data }) => data);
  },
};
